/**
 * @voltxampere/fx — Meteor particle animation (Sprint 29-3)
 *
 * Per-wire brass-hot particle pool. The renderer reads the pool every
 * frame to draw the dots; this module only owns the state machine
 * (count + position + velocity) and the per-frame step function.
 *
 * Behaviour design (v1 inspiration, v3 re-implementation):
 *   - v1 ran 3 fixed particles per visible wire, fixed velocity 0.8.
 *   - v3 scales count + velocity by current magnitude (log10) so the
 *     visual matches the physical: 1 µA wire shows few slow dots,
 *     1 A bus shows many fast dots.
 *   - Direction comes from `WireFxState.direction` (sign of branch I).
 *
 * `reduced motion` discipline (Sprint 29-5 wires the three guards):
 *   - User AYARLAR toggle off → caller skips `stepMeteorPool` entirely
 *   - `prefers-reduced-motion: reduce` → same
 *   - DC mode (no transient) → fxFrame is empty, pool stays unstepped
 *
 * Even when reached, `stepMeteorPool(..., true)` is a no-op so the
 * caller can pass the flag through one place rather than branching.
 *
 * Performance budget (60 fps, 50 wires × 8 particles = 400 updates/frame):
 *   ~10 µs CPU per frame for the position update — well under the 5 ms
 *   render budget.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/** Empty pool factory — call once when the canvas mounts. */
export function createMeteorPool() {
    return { perWire: new Map() };
}
/**
 * Compute target particle count for a wire from its current magnitude.
 *
 * Below the noise floor (1 µA) the wire shows nothing — no animation
 * needed for currents the user cannot meaningfully read off the meter.
 * Above the floor we scale logarithmically so each decade of current
 * adds ~2 particles, capped at 8 to keep the renderer cheap.
 *
 * Sentinel rows (Sprint 29-3 frozen mappings):
 *   absI = 0           → 0 particles
 *   absI = 1e-7 A      → 0 (below floor)
 *   absI = 1e-6 A      → 3 (at floor)
 *   absI = 1e-3 A      → 9 → capped 8 (3 decades × 2 + base 3 = 9)
 *   absI = 1   A       → 8 (capped — would be 15 unbounded)
 */
export function particleCountForCurrent(absI) {
    const I_THRESHOLD = 1e-6;
    if (absI < I_THRESHOLD)
        return 0;
    const decades = Math.log10(absI / I_THRESHOLD);
    const raw = Math.floor(3 + decades * 2);
    return Math.min(8, Math.max(3, raw));
}
/**
 * Compute particle velocity (path-units per second, magnitude only) from
 * current magnitude. The caller multiplies by `direction` to get the
 * signed velocity actually written to the particle.
 *
 * Reference points:
 *   1 mA → 0.8 path/sec (matches v1 fixed velocity feel)
 *   10 mA → ~1.2 path/sec (one extra decade pushes 0.4 faster)
 *   100 mA+ → cap at 2.0 (visual would otherwise blur into a streak)
 *   1 µA → cap at 0.2 (slow drift — but visible, not frozen)
 */
export function velocityForCurrent(absI) {
    const REF_I = 1e-3;
    const REF_V = 0.8;
    const decades = Math.log10(Math.max(absI, 1e-12) / REF_I);
    const scaled = REF_V + decades * 0.4;
    return Math.max(0.2, Math.min(2.0, scaled));
}
/**
 * Reconcile a wire's particle list to the target count.
 *
 * Adds new particles staggered by `i / count` so they appear evenly
 * spaced along the path; pops oldest to shrink. Stable seed lets the
 * renderer keep per-particle tints / sizes consistent across frames.
 */
export function reconcileParticles(pool, wireId, targetCount) {
    const existing = pool.perWire.get(wireId);
    const list = existing ?? [];
    while (list.length < targetCount) {
        const i = list.length;
        list.push({
            wireId,
            position: targetCount > 0 ? i / targetCount : 0,
            velocity: 0,
            seed: Math.random(),
        });
    }
    while (list.length > targetCount)
        list.pop();
    if (!existing)
        pool.perWire.set(wireId, list);
}
/** Drop every particle. Called from preset-loader to clear stale pool. */
export function clearMeteorPool(pool) {
    pool.perWire.clear();
}
/**
 * Advance every particle in the pool by `dt` seconds.
 *
 * - `wireFx` provides current + direction per wire. Wires absent from the
 *   map (e.g. removed from the circuit) are SKIPPED but their particles
 *   stay in the pool — the caller is responsible for `reconcileParticles`
 *   eviction.
 * - When `direction === 0` the wire's particles freeze in place this frame
 *   (no zero-velocity drift artefact).
 * - When `reducedMotion === true` the entire step is a no-op so the caller
 *   can route the user's accessibility preference through one place.
 *
 * Position wraps at 1.0 → 0.0 (forward) and 0.0 → 1.0 (reverse) so the
 * meteor flow looks continuous regardless of how many particles share
 * the wire.
 */
export function stepMeteorPool(pool, wireFx, dt, reducedMotion) {
    if (reducedMotion)
        return;
    for (const [wireId, particles] of pool.perWire) {
        const fx = wireFx.get(wireId);
        if (!fx || fx.direction === 0)
            continue;
        const speed = velocityForCurrent(Math.abs(fx.current));
        const v = speed * fx.direction;
        for (const p of particles) {
            p.position += v * dt;
            // Wrap into [0, 1)
            if (p.position >= 1.0)
                p.position -= Math.floor(p.position);
            else if (p.position < 0.0)
                p.position -= Math.floor(p.position);
            p.velocity = v;
        }
    }
}
//# sourceMappingURL=meteor.js.map